import React from "react";
import { Box, Stack, Typography, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { StatusDotIcon } from "../../../../components/mui/index.js";

const positiveInvoiceActions = [
  "update_sms_overage",
  "update_customer_overage",
  "create_qb_mailer_invoice",
];

export function generateLogsTooltip(changelogs, invoices = []) {
  const multipleInvoices = invoices.length > 1;
  return (
    <Box
      sx={{
        maxHeight: 300,
        overflowY: "auto",
        p: 1,
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#bbb",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
      }}
    >
      <Stack>
        {changelogs.map((log) => {
          let docNumber = null;
          if (multipleInvoices && log.quickbooks_invoice_id) {
            const inv = invoices.find(
              (i) => i.Id === log.quickbooks_invoice_id
            );
            docNumber = inv?.DocNumber;
          }
          return (
            <Stack
              key={log.id}
              spacing={1}
              sx={{ mb: 1, borderBottom: "1px solid #ccc" }}
            >
              {multipleInvoices && (
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Doc #: {docNumber ?? "--"}
                </Typography>
              )}
              <Typography variant="body2">
                {log.result_status === "error" ? "❌" : "✅"}{" "}
                {dayjs(log.created_when).format("MMM D, YYYY h:mma")}:{" "}
              </Typography>
              <Typography variant="body2">{log.action}</Typography>
              <Typography variant="body2">
                {log.changed_fields ? JSON.stringify(log.changed_fields) : ""}
              </Typography>
              <Typography variant="body2">{log.result_message}</Typography>
              {log.result_message?.includes("Craig Carlson") && (
                <Typography variant="body2">
                  This means the invoice is likely open in another web browser
                  and cannot be saved via the api while it is open
                </Typography>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}

export function buildTooltipSummary(invoice, logs, disableMaxHeight) {
  const sentLog = logs.find(
    (log) =>
      log.result_status === "success" &&
      log.action.includes("send_customer_invoice")
  );

  return (
    <Box
      sx={{
        maxHeight: disableMaxHeight ? "none" : 300,
        overflowY: "auto",
        p: 1,
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#bbb",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
      }}
    >
      <Stack spacing={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Invoice Summary
        </Typography>
        <Stack>
          <Typography sx={{ textDecoration: "underline" }}>
            Line Item Updates:
          </Typography>
          {logs?.filter(
            (log) =>
              log.result_status === "success" &&
              positiveInvoiceActions.includes(log.action)
          ).length > 0 ? (
            <Stack>
              {!!logs?.find(
                (log) =>
                  log.result_status === "success" &&
                  log.action === "create_qb_mailer_invoice"
              ) ? (
                <Typography variant="body2">
                  ✅ - Mailer Overage Updated{" "}
                </Typography>
              ) : (
                <Typography variant="body2">
                  ❌ - No Mailer overage updates found{" "}
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2">No invoice updates found</Typography>
          )}
        </Stack>
        <Stack>
          <Typography sx={{ textDecoration: "underline" }}>
            Billed Status:
          </Typography>
          {logs?.some(
            (log) =>
              log.action === "charge_stripe_customer" &&
              log.result_status === "success"
          ) ? (
            <Typography variant="body2">
              ✅ - Billed to Customer on{" "}
              {dayjs(
                logs?.find(
                  (log) =>
                    log.action === "charge_stripe_customer" &&
                    log.result_status === "success"
                ).created_when
              ).format("MMM D, YYYY h:mma")}
            </Typography>
          ) : (
            <Typography variant="body2">
              ❌ - Not yet billed to customer
            </Typography>
          )}
        </Stack>
        <Stack>
          <Typography sx={{ textDecoration: "underline" }}>
            Payment Status:
          </Typography>
          {invoice?.Balance === 0 ? (
            <Stack>
              <Typography variant="body2">
                ✅ - Invoice marked as Fully Paid in QuickBooks
              </Typography>
              {logs?.some(
                (log) =>
                  log.action === "create_quickbooks_payment" &&
                  log.result_status === "success"
              ) ? (
                <Typography variant="body2">
                  ✅ - QuickBooks Payment created on{" "}
                  {dayjs(
                    logs?.find(
                      (log) =>
                        log.action === "create_quickbooks_payment" &&
                        log.result_status === "success"
                    ).created_when
                  ).format("MMM D, YYYY h:mma")}
                </Typography>
              ) : (
                <Typography variant="body2">
                  ❌ - No QuickBooks Payment creation found
                </Typography>
              )}
            </Stack>
          ) : (
            <Stack>
              <Typography variant="body2">
                ❌ - Invoice not marked as Paid in QuickBooks
              </Typography>
              <Typography variant="body2">
                Current Balance: $
                {typeof invoice?.Balance === "number"
                  ? invoice?.Balance.toFixed(2)
                  : "--"}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack>
          <Typography sx={{ textDecoration: "underline" }}>
            Sent Status:
          </Typography>
          {invoice?.EmailStatus === "EmailSent" ||
          invoice?.EInvoiceStatus === "Sent" ||
          !!sentLog ? (
            <Stack></Stack>
          ) : (
            <Stack>
              <Typography variant="body2">No sending actions found</Typography>
            </Stack>
          )}
          {!!sentLog && (
            <Typography variant="body2">
              ✅ - Sent to Customer on{" "}
              {dayjs(sentLog.created_when).format("MMM D, YYYY h:mma")}
            </Typography>
          )}
          <Typography variant="body2">
            {invoice?.EmailStatus === "EmailSent" ? "✅ - " : "❌ -"} Email
            Status: {invoice?.EmailStatus}
          </Typography>
          <Typography variant="body2">
            {invoice?.EInvoiceStatus === "Sent" ? "✅ - " : "❌ -"} Invoice
            Opened by Customer:{" "}
            {invoice?.EInvoiceStatus ? invoice?.EInvoiceStatus : "NotSet"}{" "}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

export function generateTotalAmountTooltip(data) {
  return (
    <Box
      sx={{
        maxHeight: 300,
        overflowY: "auto",
        p: 1,
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#bbb",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
        Line Items
      </Typography>
      <Stack>
        {data.invoice?.Line?.filter(
          (line) => line.DetailType !== "SubTotalLineDetail"
        )
          .sort((a, b) => (a.LineNum ?? 0) - (b.LineNum ?? 0))
          .map((lineItem, index) => (
            <Stack
              key={lineItem.Id || index}
              spacing={1}
              sx={{ mb: 1, borderBottom: "1px solid #ccc" }}
            >
              <Typography variant="body2">
                <strong>Line Number:</strong> {lineItem.LineNum ?? "--"}
              </Typography>
              <Typography variant="body2">
                <strong>Description:</strong> {lineItem.Description ?? "--"}
              </Typography>
              <Typography variant="body2">
                <strong>Detail Type:</strong> {lineItem.DetailType ?? "--"}
              </Typography>
              <Typography variant="body2">
                <strong>Amount:</strong>{" "}
                {typeof lineItem.Amount === "number"
                  ? `$${lineItem.Amount.toFixed(2)}`
                  : "--"}
              </Typography>
              <Typography variant="body2">
                <strong>Line Id:</strong> {lineItem.Id ?? "--"}
              </Typography>
              {lineItem.SalesItemLineDetail && (
                <>
                  <Typography variant="body2">
                    <strong>Item:</strong>{" "}
                    {lineItem.SalesItemLineDetail.ItemRef?.name ?? "--"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Item Value:</strong>{" "}
                    {lineItem.SalesItemLineDetail.ItemRef?.value ?? "--"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Qty:</strong>{" "}
                    {lineItem.SalesItemLineDetail.Qty ?? "--"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Unit Price:</strong>{" "}
                    {typeof lineItem.SalesItemLineDetail.UnitPrice === "number"
                      ? `$${lineItem.SalesItemLineDetail.UnitPrice.toFixed(2)}`
                      : "--"}
                  </Typography>
                </>
              )}
            </Stack>
          ))}

        <Typography variant="body2">
          <strong>Subtotal:</strong>{" "}
          {data.invoice?.Line?.find(
            (line) => line.DetailType === "SubTotalLineDetail"
          )?.Amount
            ? `$${data.invoice.Line.find(
                (line) => line.DetailType === "SubTotalLineDetail"
              ).Amount.toFixed(2)}`
            : "--"}
        </Typography>

        <Typography variant="body2">
          <strong>Total Amount:</strong>{" "}
          {data.invoice?.TotalAmt
            ? `$${data.invoice.TotalAmt.toFixed(2)}`
            : "--"}
        </Typography>
      </Stack>
    </Box>
  );
}

export function generateSmsOverageTooltip(data, metaData) {
  return (
    <Box
      sx={{
        maxHeight: 300,
        overflowY: "auto",
        p: 1,
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#bbb",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
      }}
    >
      <Stack spacing={1}>
        <Typography variant="body2">
          <strong>
            {" "}
            {metaData?.dates?.overage_month
              ? `${dayjs(metaData.dates.overage_month).format(
                  "MMMM YYYY"
                )} Overages:`
              : "This Month Overage:"}{" "}
          </strong>
          {data?.overages_and_usage?.usage_sms_credits?.current
            ?.overage_usage ?? "--"}
        </Typography>

        <Typography variant="body2">
          <strong>
            {metaData?.dates?.overage_month
              ? `${dayjs(metaData.dates.overage_month).format(
                  "MMMM YYYY"
                )} Overage Unit Cost:`
              : "This Month Overage Unit Cost:"}{" "}
          </strong>
          {data?.overages_and_usage?.usage_sms_credits?.current
            ?.overage_per_unit
            ? `$${data.overages_and_usage.usage_sms_credits.current.overage_per_unit.toFixed(
                2
              )}`
            : "--"}
        </Typography>
        <Typography variant="body2">-------</Typography>
        <Typography variant="body2">
          <strong>
            {metaData?.dates?.overage_last_month
              ? `${dayjs(metaData.dates.overage_last_month).format(
                  "MMMM YYYY"
                )} Overages:`
              : "Last Month Overage:"}{" "}
          </strong>
          {data?.overages_and_usage?.usage_sms_credits?.last_month
            ?.overage_usage ?? "--"}
        </Typography>
        <Typography variant="body2">
          <strong>
            {metaData?.dates?.overage_last_month
              ? `${dayjs(metaData.dates.overage_last_month).format(
                  "MMMM YYYY"
                )} Overage Unit Cost:`
              : "Last Month Overage Unit Cost:"}{" "}
          </strong>
          {data?.overages_and_usage?.usage_sms_credits?.last_month
            ?.overage_per_unit
            ? `$${data.overages_and_usage.usage_sms_credits.last_month.overage_per_unit.toFixed(
                2
              )}`
            : "--"}
        </Typography>
      </Stack>
    </Box>
  );
}

export function renderInvoiceStatuses(data) {
  if (data.invoices.length === 0) {
    return (
      <Tooltip
        title={
          <Stack spacing={1}>
            <Typography>
              This either means we haven't attempted to make any changes to this
              invoice, or if some error is obvious, then not we're logging that
              error
            </Typography>
            <Typography>{buildTooltipSummary(null, [])}</Typography>
          </Stack>
        }
      >
        <Typography sx={{ fontStyle: "italic" }}> No actions found </Typography>
      </Tooltip>
    );
  }
  return (
    <Stack spacing={1} sx={{ py: 1.5 }}>
      {data.invoices.map((invoice) => {
        return (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            key={invoice.id}
          >
            {data.invoices.length > 1 && (
              <Box>
                <Typography>Invoice Num: {invoice.DocNumber}</Typography>
              </Box>
            )}

            {determineInvoiceStatus(
              invoice,
              data.invoice_logs?.filter(
                (log) => log.quickbooks_invoice_id === invoice.Id
              )
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

export function determineInvoiceStatus(invoice, logs) {
  const hasSentLog = logs?.some(
    (log) =>
      log.result_status === "success" &&
      log.action.includes("send_customer_invoice")
  );

  if (
    invoice?.EmailStatus === "EmailSent" ||
    invoice?.EInvoiceStatus === "Sent" ||
    hasSentLog
  ) {
    return (
      <Tooltip
        title={<Typography>{buildTooltipSummary(invoice, logs)}</Typography>}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontWeight: "bold" }}>Sent to Customer</Typography>
          <StatusDotIcon
            fill={
              invoice.EmailStatus === "EmailSent" && hasSentLog
                ? "#5d8ef8ff"
                : "#ffcc00"
            }
          />
        </Stack>
      </Tooltip>
    );
  }

  const hasBilledInvoiceLog = logs?.some(
    (log) =>
      log.action === "charge_stripe_customer" && log.result_status === "success"
  );
  if (hasBilledInvoiceLog) {
    return (
      <Tooltip
        title={<Typography>{buildTooltipSummary(invoice, logs)}</Typography>}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ textDecoration: "underline" }}>
            Billed to Customer
          </Typography>
          <StatusDotIcon fill={"#3bb143"} />
        </Stack>
      </Tooltip>
    );
  }

  const hasUpdatedInvoiceLog = logs?.some(
    (log) =>
      positiveInvoiceActions.includes(log.action) &&
      log.result_status === "success"
  );

  if (hasUpdatedInvoiceLog) {
    return (
      <Tooltip
        title={<Typography>{buildTooltipSummary(invoice, logs)}</Typography>}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ textDecoration: "underline" }}>
            Updated (Not Charged or Sent)
          </Typography>
          <StatusDotIcon fill={"#3bb143"} />
        </Stack>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={
        <Stack spacing={1}>
          <Typography>
            This either means we haven't attempted to make any changes to this
            invoice, or if some error is obvious, then not we're logging that
            error
          </Typography>
          <Typography>{buildTooltipSummary(invoice, logs)}</Typography>
        </Stack>
      }
    >
      <Typography sx={{ fontStyle: "italic" }}> No actions found </Typography>
    </Tooltip>
  );
}
